<template>
  <figure
    class="medium"
    :class="{
      'medium-list': ui === 'list' || ui === 'latest',
      'medium-grid': ui === 'grid',
      'medium-full': ui === 'full',
      'medium-slide': ui === 'slide',
      'medium-museum': onMuseum,

      'medium-user': ui !== 'full' && medium.isUserUpload,
      'medium-default': ui !== 'full' && !medium.isUserUpload
    }"
  >
    <div class="medium-medium">
      <img
        v-if="ui === 'full'"
        class="medium-medium-placeholder"
        :src="
          'data:image/svg+xml;utf8,<svg xmlns=&quot;http://www.w3.org/2000/svg&quot; width=&quot;' +
          medium.imageSize.width +
          '&quot; height=&quot;' +
          medium.imageSize.height +
          '&quot;></svg>'
        "
      />
      <div class="medium-medium-medium">
        <img
          v-if="!fullUI || !medium.isVideo"
          :src="mediaServer + mediumSrc"
          class="media-img"
          loading="lazy"
          alt=""
        />
        <svg v-if="!fullUI && medium.isVideo" class="video-icon">
          <use href="@/assets/symbols.svg#play" />
        </svg>
        <video
          v-if="fullUI && medium.isVideo"
          :poster="mediaServer + medium.thumbURL"
          :src="mediaServer + medium.imgURL"
          controls
          playsinline
        />
      </div>
    </div>

    <figcaption v-if="ui === 'list' || ui === 'latest'">
      <h2
        v-if="medium.title"
        :title="medium.latlng?.lat + ',' + medium.latlng?.lng"
      >
        {{ medium.title }}
      </h2>
      <component :is="medium.title ? 'div' : 'h2'">
        <MediumLocation
          v-if="details !== 'place'"
          :medium="medium"
          :linked="true"
        />
      </component>
      <!-- <star-rating
        v-model="rating"
        :read-only="true"
        :star-size="9"
        :show-rating="false"
        :inline="true"
        active-color="#e52416"
        border-color="#e52416"
        inactive-color="#fff"
        :border-width="0.75"
      /> -->
      <div class="medium-date textlight">
        {{ $d(medium.ts, 'dateshort') }} {{ $d(medium.ts, 'timeshort') }}
      </div>
      <span v-if="details !== 'author' && medium.isUserUpload" class="textlight"
        >{{ medium.authorName }}<br
      /></span>
    </figcaption>

    <figcaption v-if="ui === 'grid'">
      <h2>
        <span class="hide-s">
          {{ medium.title }}
        </span>
      </h2>
      <span class="medium-date"
        >{{ $d(medium.ts, 'dateshort') }} {{ $d(medium.ts, 'timeshort') }}</span
      >
    </figcaption>

    <figcaption v-if="ui === 'full'" class="pagemargin">
      <div class="medium-figtop">
        <span class="medium-date"
          >{{ $d(medium.ts, 'dateshort') }}
          <span class="space05before">{{
            $d(medium.ts, 'timeshort')
          }}</span></span
        >
        <h2 v-if="medium.title">
          {{ medium.title }}
        </h2>
        <component :is="medium.title ? 'div' : 'h2'">
          <MediumLocation
            v-if="!['latlng', 'place'].includes(details)"
            :medium="medium"
            :linked="!(onMuseum && details === 'user')"
          />
        </component>
      </div>
      <div v-if="medium.isUserUpload && medium.author && details !== 'user' && !author">
        <router-link
          @click.stop=""
          :to="{
            name: 'author',
            params: { id: medium.author.id }
          }"
          class="textlight textcolor textbutton"
        >
          {{ medium.authorName }}
        </router-link>
      </div>
      <span v-tippy="ratingEnabled ? null : $t('rating_disabled')">
        <VueStarRate
          v-model="rating"
          @change="addRating"
          :readonly="!ratingEnabled"
          size="sm"
          icon-provider="custom"
          :colors="{
            empty: '#aaa',
            filled: '#e52416',
            hover: '#e52416',
            half: '#71717a'
          }"
          class="medium-full-rating"
        >
          <template #icon="{ filled, size, color }">
            <svg :width="size.width" :height="size.height" viewBox="0 0 24 24">
              <polygon
                points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9"
                :fill="filled ? color : 'none'"
                :stroke="color"
                stroke-width="2"
              />
            </svg>
          </template>
        </VueStarRate>
      </span>
      <div v-if="medium.description" class="medium-description">
        {{ medium.description }}
      </div>
    </figcaption>

    <figcaption v-if="ui === 'slide'">
      <h2>{{ medium.title }}</h2>
      <div>
        <MediumLocation :medium="medium" :linked="false" />
      </div>
      <div v-if="medium.isUserUpload" class="medium-author">
        {{ medium.authorName }}
      </div>
      <span v-tippy="ratingEnabled ? null : $t('rating_disabled')">
        <VueStarRate
          v-model="rating"
          @change="addRating"
          :readonly="!ratingEnabled"
          size="sm"
          icon-provider="custom"
          :colors="{
            empty: '#aaa',
            filled: '#e52416',
            hover: '#e52416',
            half: '#71717a'
          }"
          class="medium-full-rating"
        >
          <template #icon="{ filled, size, color }">
            <svg :width="size.width" :height="size.height" viewBox="0 0 24 24">
              <polygon
                points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9"
                :fill="filled ? color : 'none'"
                :stroke="color"
                stroke-width="2"
              />
            </svg>
          </template>
        </VueStarRate>
      </span>
      <div class="medium-date">
        {{ $d(medium.ts, 'dateshort') }} {{ $d(medium.ts, 'timeshort') }}
      </div>
    </figcaption>
  </figure>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import util from '@/util'

import MediumLocation from '@/components/MediumLocation.vue'

import { VueStarRate } from 'vue-js-star-rating'
import 'vue-js-star-rating/dist/style.css'

export default {
  name: 'Medium',
  components: {
    MediumLocation,
    VueStarRate
  },
  props: ['medium', 'ui', 'details'],
  emits: ['click'],
  computed: Object.assign(
    mapState({
      ...mapGetters(['getPlaceById', 'getAuthorById']),
      mediaServer: (state) => state.mediaServer,
      author: (state) => state.author,
      user: (state) => state.user,
      museum: (state) => state.museum
    }),
    {
      onMuseum() {
        return this.museum || this.$route.name === 'museum'
      },
      fullUI() {
        return this.onMuseum || this.ui === 'full'
      },
      mediumSrc() {
        return this.medium[this.fullUI ? 'imgURL' : 'thumbURL']
      },
      mediumPlace() {
        return this.getPlaceById(this.medium.placeId)
      },
      ratingEnabled() {
        return this.user && this.user.id !== this.medium.authorId
      }
    }
  ),
  data() {
    return {
      rating: this.medium.rating
    }
  },
  methods: {
    addRating(rating) {
      this.medium
        .addRating(rating)
        .then(() => {})
        .catch((e) => util.notifyErrors(e, 'major'))
    }
  }
}
</script>

<style>
.medium {
  height: 100%;
  position: relative;
  margin: 0;
  padding: calc(var(--pagemargin) / 2);
}
.medium-medium {
  position: relative;
  border-radius: inherit;
  background: url('data:image/svg+xml;base64,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')
    no-repeat center;
  background-size: 80px;
}
.medium .media-img,
.medium video {
  display: block;
  border-radius: inherit;
  box-shadow: var(--shadow);
}

/* placeholder for lazyload */
.medium-medium-placeholder {
  display: none;
}
.medium-full .medium-medium-placeholder {
  display: block;
  position: relative;
  z-index: 1;
  box-shadow: none !important;
  max-height: calc(var(--height) * 0.75);
}
.museum .medium-full .medium-medium-placeholder {
  max-height: calc(var(--height) * 0.375);
}
.medium-full .medium-medium-medium {
  z-index: 2;
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
}
.medium-full .media-img,
.medium-full video {
  display: block;
  max-height: calc(var(--height) * 0.75);
  margin: 0 auto;
  border-radius: 0;
}

.museum .medium-medium-medium {
  height: 100%;
  border-radius: none;
}

@media (min-width: 720px) {
  .medium-full video {
    min-width: 720px;
  }
}

.medium-default .media-img,
.medium-default video {
  border-radius: var(--radius);
  filter: saturate(0.8) sepia(0.1) contrast(0.8);
}

.museum .medium-default .media-img,
.museum .medium-default video {
  border-radius: 0;
  filter: none;
}

.medium-user .media-img,
.medium-user video {
  filter: saturate(1.1) contrast(1.1);
  /* border-radius: calc(var(--radius) / 3); */
}
.video-icon {
  color: #fff;
  display: block;
  width: 50%;
  height: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  opacity: 0.6;
}

.medium-list {
  padding-bottom: 0;
}
.medium-list figcaption {
  background: #fff;
  padding: 1em 1.25em 0.5em;
  border-radius: var(--radius);
  transform: translateY(calc(-2 * var(--radius)));
  margin-bottom: calc(-1.5 * var(--radius));
}

.medium figcaption {
  font-size: 0.8571em;
}

.medium-slide {
  position: static;
  padding: 0;
  border-radius: 0;
}
.medium-slide .medium-medium {
  height: 100%;
}
.medium-slide .media-img,
.medium-slide video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  box-shadow: none;
}

.medium-grid .medium-medium {
  width: 100%;
  height: 0;
  padding-top: 75%;
}
.medium-grid .medium-medium .media-img {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.medium-grid figcaption,
.medium-slide figcaption {
  pointer-events: none;
  position: absolute;
  z-index: 2;
  left: calc(var(--pagemargin) / 2);
  right: calc(var(--pagemargin) / 2);
  bottom: 0;
  padding: 1em 1em 2em;
  color: #000;
}
.medium-grid figcaption {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
}
.medium-slide figcaption {
  bottom: 0;
  padding: 0.5em 1em;
  left: 0;
  right: 0;
  /* opacity: 0.9; */
}
.medium-grid figcaption > *,
.medium-slide figcaption > * {
  text-shadow: 0 0 0.25em #000;
  font-weight: bold;
  -webkit-text-stroke: 0.2px #000;
  -webkit-text-fill-color: #fff;
}
.medium-grid figcaption h2 {
  margin-bottom: 0;
}
.medium-full figcaption h2 {
  margin-bottom: 0.25em;
}

.medium-full .medium-medium {
  display: flex;
  align-items: flex-end;
}

.medium-museum:not(.medium-slide) .media-img,
.medium-museum:not(.medium-slide) video {
  max-height: calc(var(--height) * 0.4);
}

.medium-full figcaption {
  background: #fff;
  max-width: var(--maxcontent);
  padding: 1.5em var(--pagemargin) 3em;
  margin: 0 auto;
}

.medium-date {
  letter-spacing: 0.05em;
}
.medium-list .vue-star-rating,
.medium-slide .vue-star-rating,
.medium-full .medium-date {
  float: right;
  margin-left: 0.5em;
}

.medium .medium-description:not(:empty) {
  line-height: 1.8;
  margin: 0.5em auto 1.5em;
}

.medium-full-rating {
  margin: 1em 0;
}
</style>
